import { Bounds as MapBounds } from 'main/components/common/Map'
import {
  SchoolListingsFiltersInput,
  SchoolListingsLocationInput,
  SchoolListingsRankingFactorWeightsInput,
} from 'main/gql/globalTypes'
import { ageRangeLabelsAndValues, IFilters, IRankingFactorWeights } from 'main/util/search'
import { IIpLocation } from '../../gql/ipLocation'

export function querifySlug(slug: string | undefined) {
  return !!slug ? slug.replace('-', ', ').replace('+', ' ') : ''
}

function filtersVariables(filters: IFilters): SchoolListingsFiltersInput {
  const { enrollingAgeMonthsMin, enrollingAgeMonthsMax } = filters

  const minValue =
    enrollingAgeMonthsMin === null ? null : ageRangeLabelsAndValues[enrollingAgeMonthsMin]?.value
  const maxValue =
    enrollingAgeMonthsMax === null ? null : ageRangeLabelsAndValues[enrollingAgeMonthsMax]?.value

  return {
    acceptsSubsidy: filters.acceptsSubsidy,
    acceptsWeeklyBilling: filters.acceptsWeeklyBilling,
    hasAvailability: filters.hasAvailability,
    budgetMax: filters.budget.max,
    budgetMin: filters.budget.min,
    daysPerWeek: filters.daysPerWeek,
    dropOffEndsAt: filters.dropOffEndsAt,
    dropOffStartsAt: filters.dropOffStartsAt,
    hasUpcomingGatherings: filters.hasUpcomingGatherings,
    isTouring: filters.isTouring,
    pickUpEndsAt: filters.pickUpEndsAt,
    pickUpStartsAt: filters.pickUpStartsAt,
    selectedProgramTypes: filters.selectedProgramTypes,
    selectedNetworkRatings: filters.selectedNetworkRatings,
    selectedWhitelabelRatings: filters.selectedNetworkRatingsV2,
    selectedSpokenLanguages: filters.selectedSpokenLanguages,
    enrollingAgeMonthsMin: minValue,
    enrollingAgeMonthsMax: maxValue,
    selectedSubsidies: filters.selectedSubsidies,
    selectedProgramOfferings: filters.selectedProgramOfferings,
  }
}

function rankingFactorWeightsVariables(
  rankingFactorWeights: IRankingFactorWeights,
): SchoolListingsRankingFactorWeightsInput {
  return {
    distance: rankingFactorWeights.distance,
    freshness: rankingFactorWeights.freshness,
    touring: rankingFactorWeights.touring,
    type: rankingFactorWeights.type,
    utilization: rankingFactorWeights.utilization,
  }
}

function locationVariables(
  query: string,
  mapBounds: MapBounds | null,
  ipLocation: IIpLocation | null,
  radius: number,
): SchoolListingsLocationInput | null {
  if (mapBounds) {
    return {
      byBounds: {
        bounds: {
          northEast: {
            latitude: mapBounds.ne.lat,
            longitude: mapBounds.ne.lng,
          },
          southWest: {
            latitude: mapBounds.sw.lat,
            longitude: mapBounds.sw.lng,
          },
        },
      },
    }
  }

  if (query.trim()) {
    return { byCenter: { query, radius } }
  }

  if (ipLocation) {
    return {
      byCenter: { ip: true, radius },
    }
  }

  return null
}

interface IQueryVariablesProps {
  query: string
  filters: IFilters
  offset: number
  mapBounds: MapBounds | null
  rankingFactorWeights: IRankingFactorWeights | null
  ipLocation: IIpLocation | null
  radius: number
}

export function buildQueryVariables(props: IQueryVariablesProps) {
  const { filters, query, mapBounds, offset, rankingFactorWeights, ipLocation, radius } = props

  return {
    filters: filtersVariables(filters),
    limit: 24,
    loadAllCoordinates: true,
    location: locationVariables(query, mapBounds, ipLocation, radius),
    offset,
    rankingFactorWeights: rankingFactorWeights
      ? rankingFactorWeightsVariables(rankingFactorWeights)
      : rankingFactorWeights,
  }
}
